import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { Box, IconButton, Tooltip, Typography, TypographyProps } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CopyIcon } from '@tom-ui/ui'
import { MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { formatContainerNumber } from '../../lib/format-container-number'

export interface ContainerNumberProps {
  /** Raw, unspaced container number (e.g. "ISCU0708002"). Copied verbatim. */
  value?: string | null
  /** Display the ISO 6346 spaced form. Defaults to true; pass false in searchable table cells. */
  format?: boolean
  variant?: TypographyProps['variant']
  /** Style overrides forwarded to the number's Typography (e.g. fontWeight, fontSize). */
  sx?: TypographyProps['sx']
  'data-cy'?: string
  /** Custom display node (e.g. search-highlighted text). Overrides the formatted text; copy still uses `value`. */
  children?: ReactNode
}

const FEEDBACK_MS = 1500

export const ContainerNumber = ({
  value,
  format = true,
  variant = 'body2',
  sx,
  'data-cy': dataCy,
  children,
}: ContainerNumberProps) => {
  const { t } = useTranslate()
  const [copied, setCopied] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(timeoutRef.current), [])

  const content = children ?? (value ? (format ? formatContainerNumber(value) : value) : '')

  const handleCopy = useCallback(
    async (event: MouseEvent) => {
      // Do not trigger row click / expand handlers when copying.
      event.stopPropagation()
      if (!value || !navigator.clipboard) return
      const button = event.currentTarget as HTMLElement
      try {
        await navigator.clipboard.writeText(value)
        // A real pointer click (detail > 0) also focuses the button; drop that focus so
        // it doesn't keep the button visible after the pointer leaves. Keyboard activation
        // (detail 0) keeps focus so the confirmation stays visible until the user tabs away.
        if (event.detail > 0) button.blur()
        // Pin the confirmation visible for a fixed period, independent of hover.
        setCopied(true)
        clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => setCopied(false), FEEDBACK_MS)
      } catch {
        // Clipboard unavailable (insecure context) or permission denied — fail silently.
      }
    },
    [value],
  )

  // Visible while hovered/focused OR while the copy confirmation is active. Driving
  // this from state (not CSS :hover/:focus-within) keeps the green checkmark pinned
  // for the full timeout even after the pointer leaves, and avoids a flash of the
  // copy icon on the way out — it only reverts once the timer clears `copied`.
  const showButton = hovered || focused || copied

  if (!value) {
    return (
      <Typography component='span' variant={variant} data-cy={dataCy} sx={sx}>
        {content}
      </Typography>
    )
  }

  return (
    <Box
      component='span'
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.25,
      }}
    >
      <Typography component='span' variant={variant} data-cy={dataCy} sx={sx}>
        {content}
      </Typography>
      <Tooltip title={copied ? t('copied', 'Copied') : t('copy', 'Copy')}>
        <IconButton
          className='container-number-copy'
          size='small'
          onClick={handleCopy}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-label={t('copyContainerNumber', 'Copy container number')}
          data-cy={dataCy ? `${dataCy}-copy` : 'container-number-copy'}
          sx={{
            // Occupies its slot always (visibility via opacity) so revealing never shifts layout.
            opacity: showButton ? 1 : 0,
            //transition: 'opacity 0.15s ease-in-out',
            padding: 0.25,
            fontSize: '1rem',
          }}
        >
          {copied ? (
            <CheckRoundedIcon color='success' fontSize='inherit' />
          ) : (
            <CopyIcon fontSize='inherit' />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  )
}
